import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../services/api.service';
//import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Divider,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import FeaturesIcon from '@mui/icons-material/Extension';
import ImageIcon from '@mui/icons-material/Image';

const TenantEdit = () => {
  const { tenantAlias } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    tenant_name: '',
    tenant_code: '',
    si_id: '',
    description: '',
    active: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Fetch tenant data
  useEffect(() => {
    const fetchTenant = async () => {
      try {
        console.log(`Fetching tenant data for: ${tenantAlias}`);
        const response = await apiService.get(`/admin/developer/tenant/${tenantAlias}`);
        
        console.log('Tenant data response:', response);
        
        // Check if response has the expected structure
        if (!response || typeof response !== 'object') {
          throw new Error('Invalid response format');
        }
        
        // Extract tenant data with fallbacks for all properties
        setFormData({
          tenant_name: response.tenant_name || '',
          tenant_code: response.tenant_code || '',
          si_id: response.si_id || 'SI-HIOSAI-001',
          description: response.description || '',
          active: response.active !== false // Default to true if not specified
        });
        
        setError(null);
      } catch (err) {
        console.error('Failed to fetch tenant:', err);
        setError('Failed to load tenant data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (tenantAlias) {
      fetchTenant();
    } else {
      setError('No tenant ID provided');
      setLoading(false);
    }
  }, [tenantAlias]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'active' ? checked : value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    
    try {
      // Validate form data
      if (!formData.tenant_name || !formData.tenant_code || !formData.si_id) {
        throw new Error('Please fill in all required fields');
      }
      
      // Submit to API
      await apiService.put(`/admin/developer/tenant/${tenantAlias}`, formData);
      
      setSuccess(true);
      
      // Redirect to tenant management after a short delay
      setTimeout(() => {
        navigate('/developers/tenant-management');
      }, 1500);
    } catch (err) {
      console.error('Failed to update tenant:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to update tenant');
    } finally {
      setSaving(false);
    }
  };

  // Handle tenant deletion
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this tenant? This action cannot be undone.')) {
      return;
    }
    
    try {
      await apiService.delete(`/api/v1/admin/developer/tenant/${tenantAlias}`);
      navigate('/developers/tenant-management');
    } catch (err) {
      console.error('Failed to delete tenant:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to delete tenant');
    }
  };

  // Navigate to tenant features
  const handleManageFeatures = () => {
    navigate(`/developers/tenant-features/${tenantAlias}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/developers/tenant-management')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" component="h1">
          Edit Tenant
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button 
          variant="outlined" 
          color="primary"
          startIcon={<FeaturesIcon />}
          onClick={handleManageFeatures}
          sx={{ mr: 2 }}
        >
          Manage Features
        </Button>
        <Button 
          variant="outlined" 
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleDelete}
        >
          Delete
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<ImageIcon />}
          onClick={() => navigate(`/developers/tenant/${tenantAlias}/logo`)}
          sx={{ mr: 1 }}
        >
          Manage Logos
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Tenant updated successfully!
        </Alert>
      )}
      
      <Paper sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Tenant ID
          </Typography>
          <Chip label={tenantAlias} />
        </Box>
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Tenant Name *"
                name="tenant_name"
                value={formData.tenant_name}
                onChange={handleChange}
                fullWidth
                required
                disabled={saving}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Tenant Code *"
                name="tenant_code"
                value={formData.tenant_code}
                onChange={handleChange}
                fullWidth
                required
                disabled={true} // Tenant code should not be editable
                helperText="Tenant code cannot be changed"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="si-id-label">System Integrator *</InputLabel>
                <Select
                  labelId="si-id-label"
                  name="si_id"
                  value={formData.si_id}
                  onChange={handleChange}
                  disabled={saving}
                  required
                >
                  <MenuItem value="SI-HIOSAI-001">HIOSAI (SI-HIOSAI-001)</MenuItem>
                  <MenuItem value="SI-PARTNER-001">Partner 1 (SI-PARTNER-001)</MenuItem>
                  <MenuItem value="SI-PARTNER-002">Partner 2 (SI-PARTNER-002)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    name="active"
                    checked={formData.active}
                    onChange={handleChange}
                    disabled={saving}
                  />
                }
                label="Active"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={4}
                disabled={saving}
              />
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 3 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="contained" 
              color="primary" 
              type="submit"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default TenantEdit; 